import {getFormMessages} from '@wix/wix-events-commons-statics'
import {detailsPageDataHooks as DH} from '@wix/wix-events-data-hooks'
import React, {useMemo, useRef} from 'react'
import {useAppActions, useAppSelector} from '../../../hooks/runtime'
import {getExpandedTicketIndex, getTicketDetails, isAllTicketDetailsValid} from '../../../selectors/checkout'
import {getEvent} from '../../../selectors/event'
import {getSelectedTicketsArray} from '../../../selectors/selected-tickets'
import {REGISTRATION_FORM_ID} from '../../form/constants'
import {FormSubmitButton} from '../../form-submit-button'
import {TicketsDetailsNewProps} from './interfaces'
import {TicketDetails} from './ticket-details'
import {TicketsDetailsContext} from './tickets-details-context'
import s from './tickets-details.scss'

export const TicketsDetailsNew = ({editing, t, onSubmit}: TicketsDetailsNewProps) => {
  const selectedTickets = useAppSelector(getSelectedTicketsArray)
  const details = useAppSelector(state => selectedTickets.map((_, index) => getTicketDetails(state, index)))
  const allTicketDetailsValid = useAppSelector(isAllTicketDetailsValid)
  const messages = useAppSelector(state => getFormMessages(getEvent(state)))
  const expandedTicketIndex = useAppSelector(getExpandedTicketIndex)
  const {nextFormClicked, setExpandedTicketIndex} = useAppActions()

  const submitExpandedFormRef = useRef<(() => void) | null>(null)

  const ticketFormSubmitHandlers = useMemo(
    () => ({
      setExpandedFormSubmit: (submitFn: () => void) => {
        submitExpandedFormRef.current = submitFn
      },
      resetExpandedFormSubmit: () => {
        submitExpandedFormRef.current = () => {}
      },
    }),
    [],
  )

  const toggleExpandedTicket = (index: number) => {
    setExpandedTicketIndex(expandedTicketIndex === index ? -1 : index)
  }

  const expandNextTicket = () => {
    nextFormClicked()
  }

  const collapseTickets = () => {
    setExpandedTicketIndex(-1)
  }

  const submit = () => {
    onSubmit(details)

    if (allTicketDetailsValid) {
      collapseTickets()
    } else {
      setTimeout(() => submitExpandedFormRef.current?.(), 10)
    }
  }

  return (
    <TicketsDetailsContext.Provider value={ticketFormSubmitHandlers}>
      <div className={s.container}>
        {selectedTickets.map((ticket, index) => (
          <TicketDetails
            key={index}
            formId={`${REGISTRATION_FORM_ID}${index}`}
            index={index}
            ticket={ticket}
            editable={editing}
            expanded={index === expandedTicketIndex}
            hideButton={index === selectedTickets.length - 1}
            onExpand={() => toggleExpandedTicket(index)}
            onSubmit={expandNextTicket}
          />
        ))}
        {editing && (
          <div className={s.button}>
            <FormSubmitButton
              text={messages.checkout.submitActionLabel || messages.rsvp.submitActionLabel}
              onClick={submit}
              t={t}
              dataHook={DH.FORM_BUTTON}
            />
          </div>
        )}
      </div>
    </TicketsDetailsContext.Provider>
  )
}
